import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpResponse,
    HttpErrorResponse,
} from "@angular/common/http";
import { from, Observable, throwError } from "rxjs";
import { map, catchError, mergeMap, share } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { EnumService } from "../services/enum.service";
import { SharedDataService } from "../services/shared-data.service";
import { AccountService } from "../services/account.service";
import { Response } from "../_models/response";
import { User } from "../_models/user";

// A 128-character base64 blob used to sit here in a comment. It had the exact shape of the
// value GenerateToken returns on the server, and until the expiry bug was fixed those tokens
// never expired - so if it was a real token it was still valid years after being committed.
// Removed. It remains in this repository's history, so treat it as burned and rotate the
// platform access key rather than relying on this deletion.
@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
    user: User;

    constructor(
        public sharedDataService: SharedDataService,
        private accountService: AccountService
    ) { }

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const accessID = this.sharedDataService.deviceUID;
        const token = localStorage.getItem(
            EnumService.LocalStorageKeys.API_TOKEN
        );

        // Authentication by setting header with token value
        if (
            accessID &&
            token &&
            !request.headers.has("accessID") &&
            !request.headers.has("token")
        ) {
            request = request.clone({
                setHeaders: {
                    accessID,
                    token,
                },
            });
        }

        // if (!request.headers.has('Content-Type')) {
        //     request = request.clone({
        //         setHeaders: {
        //             'content-type': 'application/json'
        //         }
        //     });
        // }

        request = request.clone({
            headers: request.headers.set("Accept", "application/json"),
        });

        // Per-user session token. The accessID/token pair above only proves this client has the
        // global platform access key; it says nothing about which user is calling, which is why
        // the API previously trusted the userId in the request body. This is bound to one user,
        // expires, and is revoked server-side on logout or password change.
        //
        // Persisted as part of USER_DATA by AccountService.login, so it survives a restart.
        const sessionToken = this.accountService.userValue?.sessionToken;
        if (sessionToken && !request.headers.has("SessionToken")) {
            request = request.clone({
                headers: request.headers.set("SessionToken", sessionToken),
            });
        }

        if (!this.sharedDataService.dedicatedMode) {
            if (
                this.accountService.userValue?.mobileAppLanguageID ||
                this.sharedDataService.currentLanguageId ||
                this.sharedDataService.userProfile?.mobileAppLanguageID
            ) {
                const langId =
                    this.sharedDataService.userProfile?.mobileAppLanguageID ||
                    this.sharedDataService.currentLanguageId ||
                    this.accountService.userValue?.mobileAppLanguageID;
                request = request.clone({
                    headers: request.headers.set(
                        "languageID",
                        langId.toString()
                    ),
                });
            }
            if (
                this.accountService.userValue?.userId ||
                this.sharedDataService.userId
            ) {
                const userId =
                    this.accountService.userValue?.userId ||
                    this.sharedDataService.userId;
                request = request.clone({
                    headers: request.headers.set("userID", userId),
                });
            }
        } else {
            if (this.sharedDataService.dedicatedModeUserDetail?.userId) {
                request = request.clone({
                    headers: request.headers.set(
                        "userID",
                        this.sharedDataService.dedicatedModeUserDetail?.userId
                    ),
                });
            } else if (
                this.sharedDataService.dedicatedModeGuestDetail?.guestPhone
            ) {
                request = request.clone({
                    headers: request.headers.set(
                        "userID",
                        String(this.sharedDataService.dedicatedModeGuestDetail
                            ?.guestPhone)
                    ),
                });
            }
        }

        return next.handle(request).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    // console.log("event--->>>", event);
                }
                return event;
            }),
            catchError((err: HttpErrorResponse) => {
                // if token expired then get new token and resend request again
                const error: Response = err.error;

                if (error) {
                    if (err.status === EnumService.ApiResponseCode.InactiveUser) {
                        let userid = localStorage.getItem(
                            EnumService.LocalStorageKeys.API_TOKEN
                        );
                        if (userid) {
                            this.accountService.logoutInactive(userid).subscribe(
                                () => {
                                    Object.keys(EnumService.LocalStorageKeys).map((LocalStorageKey) => {
                                        if (
                                            LocalStorageKey !== EnumService.LocalStorageKeys.API_ACCESS_KEY &&
                                            LocalStorageKey !== EnumService.LocalStorageKeys.API_TOKEN
                                        ) {
                                            localStorage.removeItem(LocalStorageKey);
                                        }
                                    });
                                    this.sharedDataService.resetAllSharedVariable();
                                    userid = '';

                                },
                                (error) => {
                                    console.log('err', error)
                                }
                            );
                        }
                    }
                    if (
                        error.StatusCode ===
                        EnumService.ApiResponseCode.InvalidToken
                    ) {
                        return from(this.accountService.getToken()).pipe(
                            mergeMap((res: any) => {
                                request = request.clone({
                                    setHeaders: {
                                        accessID,
                                        token: res.token,
                                    },
                                });
                                return this.intercept(request, next);
                            })
                        );
                    }
                    let errorMessage = "";
                    if (
                        error.ResponseException &&
                        error.ResponseException.ValidationErrors &&
                        error.ResponseException.ValidationErrors.length > 0
                    ) {
                        error.ResponseException.ValidationErrors.map((data) => {
                            const errorField =
                                this.sharedDataService
                                    .companyLangaugeTranslations[data.Field] ||
                                data.Field;
                            const errorData =
                                this.sharedDataService
                                    .companyLangaugeTranslations[
                                data.Message
                                ] || data.Message;

                            errorMessage =
                                errorMessage +
                                errorField +
                                " : " +
                                errorData +
                                "\n";
                        });
                    }

                    return throwError({
                        error,
                        message:
                            errorMessage ||
                            error?.ResponseException?.ExceptionMessage ||
                            err.message,
                        status: err.status,
                    });
                }
                return throwError({
                    error: err,
                    message: err.message,
                    status: err.status,
                });
            })
        );
    }
}
